import React from 'react';
import {
    VStack,
    FormControl,
    FormLabel,
    Input,
    Button,
    Text,
    Box,
    Divider,
    HStack,
    Switch,
    Select,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
} from '@chakra-ui/react';
import { FiGitMerge } from 'react-icons/fi';

export default function MergeProperties({ nodeConfig, setNodeConfig, updateNodeData, setSelectedNode }) {
    const inputCount = nodeConfig.inputCount || 3;

    return (
        <VStack spacing={4} align="stretch">
            {/* Section 1 — Basic Settings */}
            <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider">
                Basic Settings
            </Text>

            <FormControl>
                <FormLabel fontSize="sm">Label</FormLabel>
                <Input
                    size="sm"
                    value={nodeConfig.label || ''}
                    onChange={(e) => setNodeConfig({ ...nodeConfig, label: e.target.value })}
                    placeholder="e.g., Merge Branches"
                />
            </FormControl>

            <FormControl>
                <FormLabel fontSize="sm">Incoming Branches</FormLabel>
                <HStack spacing={3}>
                    <Button
                        size="sm"
                        variant="outline"
                        isDisabled={inputCount <= 2}
                        onClick={() => setNodeConfig({ ...nodeConfig, inputCount: Math.max(2, inputCount - 1) })}
                        w="32px"
                        minW="32px"
                        px={0}
                    >
                        −
                    </Button>
                    <Box textAlign="center" flex={1}>
                        <Text fontSize="lg" fontWeight="semibold" color="purple.700">{inputCount}</Text>
                        <Text fontSize="xs" color="gray.500">branches</Text>
                    </Box>
                    <Button
                        size="sm"
                        variant="outline"
                        isDisabled={inputCount >= 10}
                        onClick={() => setNodeConfig({ ...nodeConfig, inputCount: Math.min(10, inputCount + 1) })}
                        w="32px"
                        minW="32px"
                        px={0}
                    >
                        +
                    </Button>
                </HStack>

                {/* Connector preview */}
                <Box mt={3} p={2.5} bg="purple.50" border="1px" borderColor="purple.100" borderRadius="md">
                    <Text fontSize="11px" color="purple.600" fontWeight="medium" mb={2}>Connector preview</Text>
                    <HStack align="flex-start" spacing={3}>
                        <VStack spacing={1.5} align="flex-start">
                            {Array.from({ length: inputCount }, (_, i) => (
                                <HStack key={i} spacing={1.5}>
                                    <Box w="10px" h="10px" borderRadius="full" bg="purple.500" border="2px solid white" boxShadow="sm" />
                                    <Box h="1px" w="16px" bg="purple.300" />
                                    <Text fontSize="11px" color="purple.700">Branch {i + 1}</Text>
                                </HStack>
                            ))}
                        </VStack>
                        <HStack flex={1} align="flex-end" justify="flex-end" pt={1}>
                            <Box h="1px" flex={1} bg="purple.300" />
                            <Box w="10px" h="10px" borderRadius="full" bg="indigo.500" border="2px solid white" boxShadow="sm" />
                            <Text fontSize="11px" color="indigo.700">Out</Text>
                        </HStack>
                    </HStack>
                </Box>
            </FormControl>

            <Divider />

            {/* Section 2 — Merge Behavior */}
            <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider">
                Merge Behavior
            </Text>

            <FormControl>
                <FormLabel fontSize="sm">Continue Flow When</FormLabel>
                <Select
                    size="sm"
                    value={nodeConfig.mergeBehavior || 'any'}
                    onChange={(e) => setNodeConfig({ ...nodeConfig, mergeBehavior: e.target.value })}
                >
                    <option value="any">Any branch completes first</option>
                    <option value="all">All branches complete</option>
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                    Controls when the next step should execute.
                </Text>
            </FormControl>

            <Divider />

            {/* Section 4 — Timeout */}
            <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider">
                Timeout (Optional)
            </Text>

            <FormControl>
                <HStack justify="space-between" align="flex-start">
                    <Box flex={1}>
                        <Text fontSize="sm" fontWeight="medium" color="gray.800">Set Maximum Wait Time</Text>
                        <Text fontSize="xs" color="gray.500" mt={0.5}>
                            Move on if branches don't respond in time.
                        </Text>
                    </Box>
                    <Switch
                        size="sm"
                        isChecked={nodeConfig.timeoutEnabled || false}
                        onChange={(e) => setNodeConfig({ ...nodeConfig, timeoutEnabled: e.target.checked })}
                    />
                </HStack>
            </FormControl>

            {nodeConfig.timeoutEnabled && (
                <VStack spacing={3} align="stretch" pl={0.5}>
                    <FormControl>
                        <FormLabel fontSize="sm">Wait Maximum</FormLabel>
                        <HStack spacing={2}>
                            <NumberInput
                                size="sm"
                                min={1}
                                value={nodeConfig.timeoutValue || 30}
                                onChange={(val) => setNodeConfig({ ...nodeConfig, timeoutValue: Math.max(1, parseInt(val) || 1) })}
                                w="90px"
                            >
                                <NumberInputField />
                                <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                </NumberInputStepper>
                            </NumberInput>
                            <Select
                                size="sm"
                                value={nodeConfig.timeoutUnit || 'minutes'}
                                onChange={(e) => setNodeConfig({ ...nodeConfig, timeoutUnit: e.target.value })}
                                flex={1}
                            >
                                <option value="minutes">Minutes</option>
                                <option value="hours">Hours</option>
                            </Select>
                        </HStack>
                    </FormControl>

                </VStack>
            )}

            <Divider />

            {/* Info box */}
            <Box p={3} bg="purple.50" border="1px" borderColor="purple.200" borderRadius="md">
                <HStack spacing={2} align="flex-start">
                    <Box color="purple.600" mt={0.5} flexShrink={0}>
                        <FiGitMerge size={14} />
                    </Box>
                    <Box>
                        <Text fontSize="xs" fontWeight="medium" color="purple.700" mb={1}>
                            Merge Node
                        </Text>
                        <VStack spacing={0.5} align="stretch">
                            <Text fontSize="xs" color="purple.600">• Combines multiple branches into one path</Text>
                            <Text fontSize="xs" color="purple.600">• Choose "any" or "all" branch strategy</Text>
                            <Text fontSize="xs" color="purple.600">• Prevents duplicate flow executions</Text>
                        </VStack>
                    </Box>
                </HStack>
            </Box>

            <HStack>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedNode && setSelectedNode(null)}
                >
                    Cancel
                </Button>
                <Button
                    size="sm"
                    bg="purple.600"
                    color="white"
                    _hover={{ bg: 'purple.700' }}
                    onClick={updateNodeData}
                    flex={1}
                >
                    Apply Changes
                </Button>
            </HStack>
        </VStack>
    );
}
